import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NotificationsContext = createContext(null);
const ORDERS_KEY = "kann_orders_seen_at";
const MSGS_KEY = "kann_messages_seen_at";

export const NotificationsProvider = ({ children }) => {
  const { isAdmin } = useAuth();
  const [newOrders, setNewOrders] = useState([]);
  const [newMessages, setNewMessages] = useState([]);

  const refresh = useCallback(async () => {
    if (!isAdmin) {
      setNewOrders([]);
      setNewMessages([]);
      return;
    }
    if (!localStorage.getItem(ORDERS_KEY)) localStorage.setItem(ORDERS_KEY, new Date().toISOString());
    if (!localStorage.getItem(MSGS_KEY)) localStorage.setItem(MSGS_KEY, new Date().toISOString());
    try {
      const [o, m] = await Promise.all([
        api.get("/orders"),
        api.get("/contact").catch(() => ({ data: [] })),
      ]);
      const ot = new Date(localStorage.getItem(ORDERS_KEY)).getTime();
      const mt = new Date(localStorage.getItem(MSGS_KEY)).getTime();
      setNewOrders((o.data || []).filter((x) => new Date(x.created_at).getTime() > ot).map((x) => x.id));
      setNewMessages((m.data || []).filter((x) => new Date(x.created_at).getTime() > mt).map((x) => x.id));
    } catch {
      /* ignore */
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) {
      setNewOrders([]);
      setNewMessages([]);
      return;
    }
    refresh();
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [isAdmin, refresh]);

  const markOrdersSeen = useCallback(() => {
    localStorage.setItem(ORDERS_KEY, new Date().toISOString());
    setNewOrders([]);
  }, []);

  const markMessagesSeen = useCallback(() => {
    localStorage.setItem(MSGS_KEY, new Date().toISOString());
    setNewMessages([]);
  }, []);

  return (
    <NotificationsContext.Provider
      value={{
        newOrders,
        newMessages,
        hasNew: newOrders.length + newMessages.length > 0,
        refresh,
        markOrdersSeen,
        markMessagesSeen,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationsContext);
